import { useCallback, useContext, useMemo } from "react";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import {
    useCounterpartyDrillDownAvailableFilters,
    useCounterpartyTransactionsFilters,
} from "@hooks/useCounterpartyTransactionsOpener";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import { createDefaultCounterpartyDrillDownTransactionView } from "@components/transactions/views/DefaultCounterpartyDrillDownTransactionView";
import { useOverlays } from "contexts/OverlayContext";
import { DirectoryContext } from "./Directory.context";

/** Opens the journal entries, transactions, invoices and bills of one counterparty. */
export const useCounterpartyRecordOpeners = (counterpartyId: string) => {
    const { openGlDrawer } = useContext(DirectoryContext);
    const { openOverlay } = useOverlays();
    const openGlobalTransactionViewDrawer = useGlobalTransactionViewDrawer();
    const availableFilters = useCounterpartyDrillDownAvailableFilters();
    const transactionsFilters = useCounterpartyTransactionsFilters(
        counterpartyId,
        { includeDateFilter: false },
    );
    const isClassesEnabled = useIsClassesEnabled();
    const defaultCounterpartyDrillDownTransactionView = useMemo(
        () =>
            createDefaultCounterpartyDrillDownTransactionView({
                isClassesEnabled,
            }),
        [isClassesEnabled],
    );

    const openJournalEntries = useCallback(() => {
        if (!counterpartyId) {
            return;
        }
        openGlDrawer(counterpartyId, false);
    }, [counterpartyId, openGlDrawer]);

    const openTransactions = useCallback(() => {
        openGlobalTransactionViewDrawer(transactionsFilters, {
            baseViewOverride: defaultCounterpartyDrillDownTransactionView,
            availableFilters,
        });
    }, [
        openGlobalTransactionViewDrawer,
        transactionsFilters,
        defaultCounterpartyDrillDownTransactionView,
        availableFilters,
    ]);

    const openInvoices = useCallback(() => {
        if (!counterpartyId) {
            return;
        }
        openOverlay("invoicesDrilldown", {
            filters: { counterpartiesIds: [counterpartyId] },
        });
    }, [counterpartyId, openOverlay]);

    const openBills = useCallback(() => {
        if (!counterpartyId) {
            return;
        }
        openOverlay("billsDrilldown", {
            filters: { counterpartiesIds: [counterpartyId] },
        });
    }, [counterpartyId, openOverlay]);

    return { openJournalEntries, openTransactions, openInvoices, openBills };
};
